import { useState } from "react";
import {
  isPrinterSupported,
  printRaster,
  type PrintStats,
} from "../../platform/printer";
import {
  forgetPrinter,
  getPrinter,
  pairPrinter,
  printerStatus,
  printerTail,
  saveTuningFor,
  tuningFor,
  PRINTER_ROLES,
  roleLabel,
  type PrinterProfile,
  type PrinterRole,
} from "../../platform/printerRegistry";
import {
  tuningToJobOptions,
  type PrintDarkness,
  type PrintSpeed,
  type PrintTuning,
} from "../../platform/printSettings";
import { SegmentedControl } from "../ui/SegmentedControl";

/**
 * G-65 — one physical printer on this counter: pair it, tune it, test it.
 *
 * EXTRACTED BECAUSE THERE ARE TWO NOW. The same card serves the receipt
 * printer and the sticker printer; a copy would have to be kept in step every
 * time a dial or a message changed, and the two would drift. What differs
 * between them is only the role, the words, and what a test print puts on the
 * paper — all props.
 *
 * Each card owns its OWN busy/error state on purpose: pairing the sticker
 * printer must not grey out the receipt printer's buttons, and a failure on one
 * says nothing about the other.
 */

const SPEED_OPTIONS: Array<{ value: PrintSpeed; label: string }> = [
  { value: "standard", label: "Standard" },
  { value: "faster", label: "Faster" },
  { value: "fastest", label: "Fastest" },
];

const DARKNESS_OPTIONS: Array<{ value: PrintDarkness; label: string }> = [
  { value: "light", label: "Light" },
  { value: "normal", label: "Normal" },
  { value: "dark", label: "Dark" },
];

/**
 * Which OTHER role, if any, is pointed at this same machine.
 *
 * This is a SUPPORTED SETUP, not an error — the owner chose it on 2026-09-12
 * after pairing a second machine proved painful, and swapping the paper is a
 * perfectly good way to run one counter. So the notice below states the
 * consequence and stops; it does not tell anybody off. It is still worth
 * saying, because the other way into this state is picking the wrong "X5" from
 * a list of two identical names, and then notes arrive on till roll.
 */
function clashingRole(
  role: PrinterRole,
  deviceId: string,
): PrinterRole | null {
  return (
    PRINTER_ROLES.find(
      (other) => other !== role && getPrinter(other)?.deviceId === deviceId,
    ) ?? null
  );
}

/** Says WHERE the time went, not just that it went — reaching the printer and
 *  pushing the bytes are different problems with different fixes. */
function describe(stats: PrintStats): string {
  return [
    `Reaching the printer ${(stats.connectMs / 1000).toFixed(1)}s`,
    `sending ${(stats.transferMs / 1000).toFixed(1)}s`,
    `of which ${(stats.pacingMs / 1000).toFixed(1)}s was our own rate limit`,
    `${stats.bytes} bytes in ${stats.chunks} packets`,
    stats.flowControl
      ? `printer asked us to wait ${stats.pauses}× (${(stats.pausedMs / 1000).toFixed(1)}s)`
      : "printer offers no back-pressure channel",
  ].join(" · ");
}

export function PrinterCard({
  role,
  title,
  hint,
  buildTestJob,
  testLabel = "Print a test receipt",
  unpairedHint,
}: {
  role: PrinterRole;
  title: string;
  hint: string;
  /** What "nothing paired" MEANS for this role — absent is not always a gap. */
  unpairedHint?: string;
  /**
   * The bytes a test print puts on paper, given this printer's own tuning and
   * paper width. Null when there is nothing to print yet (a design still
   * loading), which disables the button rather than printing a blank.
   */
  buildTestJob: ((printer: PrinterProfile) => Uint8Array) | null;
  testLabel?: string;
}) {
  const supported = isPrinterSupported();
  const [printer, setPrinter] = useState<PrinterProfile | null>(() => {
    const status = printerStatus(role);
    return status.state === "paired" ? status.printer : null;
  });
  const [tuning, setTuning] = useState<PrintTuning>(() => tuningFor(role));
  const [busy, setBusy] = useState<"pair" | "print" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const handlePair = async () => {
    setBusy("pair");
    setError(null);
    setNote(null);
    try {
      const paired = await pairPrinter(role);
      setPrinter(paired);
      setTuning(paired.tuning);
      setNote(`Paired with ${paired.name ?? "the printer"}.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not pair a printer.");
    } finally {
      setBusy(null);
    }
  };

  const handleTestPrint = async () => {
    if (!printer || !buildTestJob) return;
    setBusy("print");
    setError(null);
    setNote(null);
    try {
      // Built for this printer, sent to this printer — one resolution.
      const stats = await printRaster(printer, buildTestJob(printer));
      setNote(describe(stats));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not print.");
    } finally {
      setBusy(null);
    }
  };

  const handleForget = () => {
    forgetPrinter(role);
    setPrinter(null);
    setNote("Forgotten. Pair again to use a printer from this tablet.");
  };

  /** Saved immediately: there is no Save button here, and a dial you have to
   *  remember to confirm is a dial people get wrong. */
  const applyTuning = (next: PrintTuning) => {
    setTuning(next);
    saveTuningFor(role, next);
  };

  return (
    <div className="card">
      <h2>{title}</h2>
      <p className="muted">{hint}</p>

      {printer ? (
        <p>
          Printing to <strong>{printer.name ?? "a printer"}</strong>{" "}
          <span className="muted">(…{printerTail(printer.deviceId)})</span>.
        </p>
      ) : (
        <p className="muted">{unpairedHint ?? "Nothing paired for this yet."}</p>
      )}

      {printer && clashingRole(role, printer.deviceId) ? (
        <div className="alert">
          Also your{" "}
          {roleLabel(clashingRole(role, printer.deviceId)!).toLowerCase()}.
          Receipts and notes both come out of this machine, on whatever paper is
          loaded — swap the roll to suit the job, or pair a second printer to
          keep them apart.
        </div>
      ) : null}

      {error ? <div className="alert error">{error}</div> : null}
      {note ? <div className="alert">{note}</div> : null}

      {!supported ? (
        /* Absent, not broken: a browser has no Bluetooth radio to offer, so
           saying why beats a dead button (G-27 interaction rule 5). */
        <p className="muted">
          Pairing happens on the shop tablet. Open this page in the Flower World
          app to connect a printer.
        </p>
      ) : (
        <div className="button-row">
          <button
            type="button"
            className="button"
            onClick={() => void handlePair()}
            disabled={busy !== null}
          >
            {busy === "pair"
              ? "Waiting for a printer…"
              : printer
                ? "Pair a different printer"
                : "Pair a printer"}
          </button>
          {printer ? (
            <>
              <button
                type="button"
                className="button tinted"
                onClick={() => void handleTestPrint()}
                disabled={busy !== null || !buildTestJob}
              >
                {busy === "print" ? "Printing…" : testLabel}
              </button>
              <button
                type="button"
                className="button plain"
                onClick={handleForget}
                disabled={busy !== null}
              >
                Forget this printer
              </button>
            </>
          ) : null}
        </div>
      )}

      {printer ? (
        <div className="receipt-tuning">
          <h3 className="receipt-list-heading">How it prints</h3>
          <p className="muted">
            A quicker head has less time to burn each dot, so going faster prints
            fainter. Change one, print a test, look at the paper.
          </p>
          <label className="receipt-tuning-row">
            <span>Speed</span>
            <SegmentedControl
              options={SPEED_OPTIONS}
              value={tuning.speed}
              onChange={(speed) => applyTuning({ ...tuning, speed })}
              ariaLabel={`How fast ${title} prints`}
              disabled={busy !== null}
            />
          </label>
          <label className="receipt-tuning-row">
            <span>Darkness</span>
            <SegmentedControl
              options={DARKNESS_OPTIONS}
              value={tuning.darkness}
              onChange={(darkness) => applyTuning({ ...tuning, darkness })}
              ariaLabel={`How hard ${title} burns`}
              disabled={busy !== null}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
}

/** Shared by both cards: this printer's own heat and paper width, as job bytes. */
export function jobOptionsFor(printer: PrinterProfile) {
  return {
    ...tuningToJobOptions(printer.tuning),
    rowBytes: Math.ceil(printer.widthDots / 8),
  };
}
